import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATIONS_PAGE_QUERY_KEY } from 'app/hooks/useFetchReconciliationsPage';

import ReconciliationsApiHandler from 'app/services/reconciliationsApiHandler';

import { QueueReconciliationRequest, ReconciliationRun } from 'app/types/reconciliations';

const DEFAULT_ERROR_DESCRIPTION =
  'Sorry, the reconciliation could not be started. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Queues a reconciliation of every company, or of the companies given. */
const useQueueReconciliation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<ReconciliationRun, Error, QueueReconciliationRequest>(
    (body) => ReconciliationsApiHandler.queueReconciliation(body),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Reconciliation queued' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: [RECONCILIATIONS_PAGE_QUERY_KEY] }),
    }
  );
};

export default useQueueReconciliation;
